import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const FLAG_META = {
  isrc_superseded: { label: 'ISRC has been superseded', tone: 'bad' },
  isrc_mismatch: { label: 'ISRC does not match the fingerprinted audio', tone: 'bad' },
  isrc_reused: { label: 'ISRC belongs to a different recording', tone: 'bad' },
  isrc_suggested: { label: 'Possible match by title & duration — please confirm', tone: 'warn' },
  new_recording: { label: 'No match found — looks like a new recording', tone: 'info' },
  no_audio_match: { label: 'Could not find an audio file for this track', tone: 'bad' },
  fingerprint_error: { label: 'Could not fingerprint this audio file', tone: 'bad' }
};

// Fingerprinting + high-confidence auto-fill already happened during upload
// (see server/verify.js), so this step only has to review whatever's left —
// no fingerprinting wait here, and nothing to click for the cases the tool
// was already sure about.
function VerifyStep({ sessionId, sessionData, editedData, setEditedData, onComplete }) {
  const [flags, setFlags] = useState(sessionData?.verifyFlags || null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!sessionData?.verifyFlags);
  const [resolved, setResolved] = useState({}); // trackNumber -> 'accepted' | 'kept' | 'confirmed_new'
  const [busyTrack, setBusyTrack] = useState(null);

  const runVerify = useCallback((rescan = false) => {
    setLoading(true);
    setError('');
    axios.post(`${API}/api/verify/${sessionId}`, { tracks: editedData.tracks, rescan }, { timeout: 0 })
      .then(res => setFlags(res.data.flags))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => {
    // Only hit the server if the upload response didn't already carry flags
    // (e.g. an older session, or the page was refreshed) — normally this
    // step just renders what upload already computed.
    if (!sessionData?.verifyFlags) runVerify();
    // eslint-disable-next-line
  }, []);

  const updateTrackIsrc = (trackNumber, productCode, newIsrc) => {
    setEditedData(d => ({
      ...d,
      tracks: d.tracks.map(t =>
        t.trackNumber === trackNumber && t.productCode === productCode ? { ...t, isrc: newIsrc } : t
      )
    }));
  };

  const acceptCorrection = async (flag) => {
    setBusyTrack(flag.trackNumber + flag.productCode);
    try {
      updateTrackIsrc(flag.trackNumber, flag.productCode, flag.matchedRecording.canonicalIsrc);
      setResolved(r => ({ ...r, [flag.trackNumber + flag.productCode]: 'accepted' }));
    } finally {
      setBusyTrack(null);
    }
  };

  const keepAsIs = (flag) => {
    setResolved(r => ({ ...r, [flag.trackNumber + flag.productCode]: 'kept' }));
  };

  // Accepting a title+duration suggestion both fills in the ISRC and saves
  // this audio's fingerprint onto that recording — a user-confirmed
  // suggestion is as good as a real match going forward, so future uploads
  // of the same track can fingerprint-match it directly instead of relying
  // on the weaker title/duration heuristic again.
  const acceptSuggestion = async (flag) => {
    setBusyTrack(flag.trackNumber + flag.productCode);
    try {
      updateTrackIsrc(flag.trackNumber, flag.productCode, flag.suggestedRecording.canonicalIsrc);
      await axios.post(`${API}/api/verify/${sessionId}/confirm-match`, {
        recordingId: flag.suggestedRecording.id,
        fingerprint: flag.fingerprint,
        fingerprintDuration: flag.fingerprintDuration
      });
      setResolved(r => ({ ...r, [flag.trackNumber + flag.productCode]: 'accepted' }));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyTrack(null);
    }
  };

  const rejectSuggestion = async (flag) => {
    // Not the same recording after all — treat it like any other new
    // recording: confirm it into the library under its own fingerprint.
    await confirmNew(flag);
  };

  const confirmNew = async (flag) => {
    setBusyTrack(flag.trackNumber + flag.productCode);
    try {
      await axios.post(`${API}/api/verify/${sessionId}/confirm-new`, {
        trackNumber: flag.trackNumber,
        productCode: flag.productCode,
        title: flag.trackName,
        version: '',
        artist: '',
        isrc: flag.sheetIsrc,
        fingerprint: flag.fingerprint,
        fingerprintDuration: flag.fingerprintDuration
      });
      setResolved(r => ({ ...r, [flag.trackNumber + flag.productCode]: 'confirmed_new' }));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyTrack(null);
    }
  };

  const needsAttention = (flags || []).filter(f =>
    ['isrc_superseded', 'isrc_mismatch', 'isrc_reused', 'no_audio_match', 'fingerprint_error'].includes(f.type)
  );
  const suggested = (flags || []).filter(f => f.type === 'isrc_suggested');
  const newRecordings = (flags || []).filter(f => f.type === 'new_recording');

  const allResolved = (flags || []).every(f => !!resolved[f.trackNumber + f.productCode]);
  const autoApplied = sessionData?.autoApplied || 0;

  if (loading) {
    return (
      <Center>
        <Spinner />
        <LoadingText>Checking for anything that still needs your attention...</LoadingText>
      </Center>
    );
  }

  if (error) {
    return (
      <Center>
        <ErrorBox>{error}</ErrorBox>
        <RetryBtn onClick={() => runVerify(true)}>Retry</RetryBtn>
      </Center>
    );
  }

  // Nothing left to review — everything was either auto-applied at upload
  // or there was nothing to flag in the first place. No point making the
  // user look at an empty screen; hand them straight to Export.
  if ((flags || []).length === 0) {
    return (
      <Center>
        <AllClearIcon>✓</AllClearIcon>
        <LoadingText>
          {autoApplied > 0
            ? `All clear — ${autoApplied} ISRC${autoApplied === 1 ? '' : 's'} confirmed automatically by audio fingerprint.`
            : 'All clear — nothing needed review.'}
        </LoadingText>
        <ProceedBtn onClick={() => onComplete(editedData)}>Proceed to Export →</ProceedBtn>
      </Center>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Verify against the track library</Title>
        <Subtitle>
          {autoApplied > 0 && <>{autoApplied} ISRC{autoApplied === 1 ? '' : 's'} confirmed automatically · </>}
          {needsAttention.length} need your attention · {suggested.length} possible matches to confirm · {newRecordings.length} new recordings
        </Subtitle>
      </Header>

      {needsAttention.length > 0 && (
        <Section>
          <SectionTitle warn>Needs your attention</SectionTitle>
          {needsAttention.map(f => (
            <FlagCard key={f.trackNumber + f.productCode} tone={FLAG_META[f.type]?.tone}>
              <FlagHeader>
                <FlagTrack>Track {f.trackNumber} · {f.trackName}</FlagTrack>
                <FlagBadge tone={FLAG_META[f.type]?.tone}>{FLAG_META[f.type]?.label || f.type}</FlagBadge>
              </FlagHeader>

              {f.type === 'isrc_superseded' && (
                <FlagBody>
                  <p>Credit sheet has <Mono>{f.sheetIsrc}</Mono>, which was consolidated into <Mono>{f.matchedRecording.canonicalIsrc}</Mono> for "{f.matchedRecording.title}{f.matchedRecording.version ? ` [${f.matchedRecording.version}]` : ''}".</p>
                  {resolved[f.trackNumber + f.productCode] ? (
                    <ResolvedNote>{resolved[f.trackNumber + f.productCode] === 'accepted' ? 'Corrected ✓' : 'Kept as-is'}</ResolvedNote>
                  ) : (
                    <FlagActions>
                      <PrimaryBtn disabled={busyTrack === f.trackNumber + f.productCode} onClick={() => acceptCorrection(f)}>
                        Use correct ISRC ({f.matchedRecording.canonicalIsrc})
                      </PrimaryBtn>
                      <SecondaryBtn onClick={() => keepAsIs(f)}>Keep as-is anyway</SecondaryBtn>
                    </FlagActions>
                  )}
                </FlagBody>
              )}

              {f.type === 'isrc_mismatch' && (
                <FlagBody>
                  <p>Credit sheet has <Mono>{f.sheetIsrc}</Mono>, but the audio fingerprint-matches "{f.matchedRecording.title}{f.matchedRecording.version ? ` [${f.matchedRecording.version}]` : ''}" (ISRC <Mono>{f.matchedRecording.canonicalIsrc}</Mono>) at {Math.round(f.similarity * 100)}% confidence.</p>
                  {resolved[f.trackNumber + f.productCode] ? (
                    <ResolvedNote>{resolved[f.trackNumber + f.productCode] === 'accepted' ? 'Corrected ✓' : 'Kept as-is'}</ResolvedNote>
                  ) : (
                    <FlagActions>
                      <PrimaryBtn disabled={busyTrack === f.trackNumber + f.productCode} onClick={() => acceptCorrection(f)}>
                        Use matched ISRC ({f.matchedRecording.canonicalIsrc})
                      </PrimaryBtn>
                      <SecondaryBtn onClick={() => keepAsIs(f)}>Keep sheet ISRC anyway</SecondaryBtn>
                    </FlagActions>
                  )}
                </FlagBody>
              )}

              {f.type === 'isrc_reused' && (
                <FlagBody>
                  <p>ISRC <Mono>{f.sheetIsrc}</Mono> already belongs to "{f.reusedOnRecording?.title}" in the library, but the uploaded audio doesn't fingerprint-match it — check this isn't a mix-up.</p>
                  <SecondaryBtn onClick={() => keepAsIs(f)}>Acknowledge</SecondaryBtn>
                </FlagBody>
              )}

              {(f.type === 'no_audio_match' || f.type === 'fingerprint_error') && (
                <FlagBody>
                  <p>{f.error || 'Could not locate or process the audio file for this track.'}</p>
                </FlagBody>
              )}
            </FlagCard>
          ))}
        </Section>
      )}

      {suggested.length > 0 && (
        <Section>
          <SectionTitle warn>Possible matches — please confirm</SectionTitle>
          {suggested.map(f => (
            <FlagCard key={f.trackNumber + f.productCode} tone="warn">
              <FlagHeader>
                <FlagTrack>Track {f.trackNumber} · {f.trackName}</FlagTrack>
                <FlagBadge tone="warn">Same title & duration</FlagBadge>
              </FlagHeader>
              <FlagBody>
                <p>
                  No fingerprint or ISRC match, but "{f.suggestedRecording.title}{f.suggestedRecording.version ? ` [${f.suggestedRecording.version}]` : ''}"
                  in the library has the same title and a duration within {f.suggestedRecording.durationDiff}s — ISRC <Mono>{f.suggestedRecording.canonicalIsrc}</Mono>.
                  This is a text-based suggestion, not a confirmed audio match — worth a quick listen-check if unsure.
                </p>
                {resolved[f.trackNumber + f.productCode] ? (
                  <ResolvedNote>{resolved[f.trackNumber + f.productCode] === 'accepted' ? 'Matched ✓' : 'Kept as new'}</ResolvedNote>
                ) : (
                  <FlagActions>
                    <PrimaryBtn disabled={busyTrack === f.trackNumber + f.productCode} onClick={() => acceptSuggestion(f)}>
                      Yes, same recording — use {f.suggestedRecording.canonicalIsrc}
                    </PrimaryBtn>
                    <SecondaryBtn onClick={() => rejectSuggestion(f)}>No, different recording</SecondaryBtn>
                  </FlagActions>
                )}
              </FlagBody>
            </FlagCard>
          ))}
        </Section>
      )}

      {newRecordings.length > 0 && (
        <Section>
          <SectionTitle>New recordings (not yet in the library)</SectionTitle>
          {newRecordings.map(f => (
            <FlagCard key={f.trackNumber + f.productCode} tone="info">
              <FlagHeader>
                <FlagTrack>Track {f.trackNumber} · {f.trackName}</FlagTrack>
                <FlagBadge tone="info">New</FlagBadge>
              </FlagHeader>
              <FlagBody>
                <p>No fingerprint or ISRC match found in the library{f.sheetIsrc ? <> — sheet ISRC <Mono>{f.sheetIsrc}</Mono></> : ' and no ISRC on the sheet'}.</p>
                {resolved[f.trackNumber + f.productCode] ? (
                  <ResolvedNote>Added to library ✓</ResolvedNote>
                ) : (
                  <FlagActions>
                    <PrimaryBtn disabled={busyTrack === f.trackNumber + f.productCode} onClick={() => confirmNew(f)}>
                      Confirm new — add to library
                    </PrimaryBtn>
                    <SecondaryBtn onClick={() => keepAsIs(f)}>Skip for now</SecondaryBtn>
                  </FlagActions>
                )}
              </FlagBody>
            </FlagCard>
          ))}
        </Section>
      )}

      <Footer>
        <ProceedBtn onClick={() => onComplete(editedData)}>
          {allResolved ? 'Proceed to Export →' : 'Proceed anyway →'}
        </ProceedBtn>
        {!allResolved && <FooterNote>Some flags haven't been addressed — you can still proceed if you're confident.</FooterNote>}
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 28px 24px 60px;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 10px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid var(--border);
  border-top-color: var(--blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const LoadingText = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  margin-top: 8px;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.3px;
  color: var(--text);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-top: 4px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.warn ? 'var(--amber)' : 'var(--text)'};
  margin-bottom: 10px;
`;

const FlagCard = styled.div`
  background: var(--bg-2);
  border-radius: var(--radius);
  padding: 14px 16px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 10px;
  border-left: 3px solid ${p =>
    p.tone === 'bad' ? 'var(--red)' :
    p.tone === 'warn' ? 'var(--amber)' :
    p.tone === 'info' ? 'var(--blue)' : 'var(--green)'};
`;

const FlagHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const FlagTrack = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
`;

const FlagBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 10px;
  border-radius: 10px;
  white-space: nowrap;
  background: ${p =>
    p.tone === 'bad' ? 'var(--red-dim)' :
    p.tone === 'warn' ? 'var(--amber-dim)' :
    p.tone === 'info' ? 'var(--blue-dim)' : 'var(--green-dim)'};
  color: ${p =>
    p.tone === 'bad' ? 'var(--red)' :
    p.tone === 'warn' ? 'var(--amber)' :
    p.tone === 'info' ? 'var(--blue-dark)' : 'var(--green)'};
`;

const FlagBody = styled.div`
  font-size: 12px;
  color: var(--text-2);
  line-height: 1.5;
  p { margin-bottom: 8px; }
`;

const FlagActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--text);
`;

const PrimaryBtn = styled.button`
  background: var(--gradient-ocean);
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 7px 14px;
  border-radius: var(--radius);
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; }
`;

const SecondaryBtn = styled.button`
  background: none;
  border: 1.5px solid var(--border);
  color: var(--text-2);
  font-size: 11px;
  font-weight: 700;
  padding: 7px 14px;
  border-radius: var(--radius);
  transition: all 0.15s;
  &:hover { border-color: var(--text-3); color: var(--text); }
`;

const ResolvedNote = styled.div`
  font-size: ${p => p.small ? '10px' : '12px'};
  font-weight: 700;
  color: var(--green);
`;

const AllClearIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--green-dim);
  color: var(--green);
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 30px;
`;

const ProceedBtn = styled.button`
  background: var(--gradient-sun);
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 32px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
`;

const FooterNote = styled.div`
  font-size: 11px;
  color: var(--text-3);
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 12px 18px;
  border-radius: var(--radius);
  font-size: 13px;
`;

const RetryBtn = styled.button`
  background: var(--gradient-ocean);
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: var(--radius);
`;

export default VerifyStep;
